import { z } from 'zod';

// Base screening result schema
export const ScreeningResultSchema = z.object({
  id: z.string().uuid().optional(),
  database_id: z.string().uuid(),
  screening_timestamp: z.string().datetime(),
  screening_result: z.record(z.any()), // For JSONB type
  screening_reason: z.string().optional().nullable(),
  screened_by: z.string().optional().nullable(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});

// Screening result row schema (matches IScreeningResultRow interface)
export const ScreeningResultRowSchema = z.object({
  id: z.string().uuid(),
  database_id: z.string().uuid(),
  database_name: z.string(),
  screening_timestamp: z.string().datetime(),
  screening_result: z.record(z.any()),
  screening_reason: z.string().optional().nullable(),
  screened_by: z.string().optional().nullable(),
  status: z
    .enum(['pending', 'running', 'complete', 'failed', 'in-progress'])
    .optional(),
  total_results: z.number().optional(),
  new_results: z.number().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime().optional(),
  chunks_processed: z.number().optional(),
  chunks_total: z.number().optional(),
});

// AI Results schema
export const AIResultsSchema = z.object({
  entity_name: z.string(),
  match_probability: z.number().optional(),
  risk: z.string().optional(),
  explanation: z.string().optional(),
  best_match: z.string().optional(),
  document_count: z.number().optional(),
});

// Entity screening data schema
export const EntityScreeningSchema = z.object({
  entity_id: z.string().uuid(),
  entity_name: z.string(),
  database_id: z.string().uuid(),
  screening_history_id: z.string().uuid().optional(),
});

// Product screening data schema
export const ProductScreeningSchema = z.object({
  product_id: z.string().uuid(),
  product_name: z.string(),
  product_hts_code: z.string().optional(),
  database_id: z.string().uuid(),
  screening_history_id: z.string().uuid().optional(),
});

// Run screening request schema
export const RunScreeningRequestSchema = z.object({
  databaseIds: z.array(z.union([z.string(), z.number()])),
});

export const RunSelectEntitiesRequestSchema = z.object({
  databaseId: z.string(),
  entityIds: z.array(z.string()).min(1),
});

export const RunTariffCheckRequestSchema = z.object({
  databaseIds: z.array(z.union([z.string(), z.number()])),
});

export const RunProductCheckRequestSchema = z.object({
  databaseIds: z.array(z.union([z.string(), z.number()])),
  // 'screen' recomputes tariffs as-of the run date (an explicit re-screen so we can
  // track how tariffs change over time); omitted/'entry' keeps the transaction-date
  // calc. The UI "Run Screening" action sends 'screen'.
  tariffDateMode: z.enum([ 'entry', 'screen' ]).optional(),
});

export const RunProductScreeningRequestSchema = z.object({
  databaseId: z.union([z.string(), z.number()]),
  databaseProductId: z.union([z.string(), z.number()]).optional(),
  productId: z.union([z.string(), z.number()]).optional(),
  initiatedBy: z.string().optional(),
  skipEmail: z.boolean().optional(),
});

export const RunEntityScreeningRequestSchema = z.object({
  databaseId: z.union([z.string(), z.number()]),
  databaseEntityId: z.union([z.string(), z.number()]).optional(),
  entityId: z.union([z.string(), z.number()]).optional(),
  initiatedBy: z.string().optional(),
});

const TopMatchSchema = z.object({
  id: z.string().uuid().nullable(),
  name: z.string(),
  confidence: z.number(),
});

export const AISummarySchema = z.object({
  risk: z.string(),
  best_match: z.string(),
  screenDate: z.string(),
  entity_name: z.string(),
  explanation: z.string(),
  top_matches: z.array(TopMatchSchema),
  match_probability: z.number(),
});

// Entity Screening Result schema
export const EntityScreeningResultSchema = z.object({
  id: z.string().uuid(),
  screening_result_id: z.string().uuid(),
  database_entity_id: z.string().uuid(),
  hit_count: z.number().default(0),
  new_total: z.number().default(0),
  last_screening_date: z.string().datetime().optional(),
  is_base_snapshot: z.boolean().default(false),
  all_results: z.string().optional().nullable(),
  diff_compressed: z.string().optional().nullable(),
  results_hash: z.string().optional().nullable(),
  diff_added_count: z.number().default(0),
  diff_removed_count: z.number().default(0),
  diff_modified_count: z.number().default(0),
  doc_info: z.record(z.any()).optional().nullable(),
  created_at: z.string().datetime().optional(),
  ai_summary: AISummarySchema.optional().nullable(),
  screening_timestamp: z.string().datetime().optional(),
  screening_reason: z.string().optional().nullable(),
  screened_by: z.string().optional().nullable(),
  is_latest: z.boolean().optional(),
});

// Entity Screening History Response schema
export const EntityScreenHisResSchema = z.object({
  latestResult: EntityScreeningResultSchema,
  deltas: z.array(EntityScreeningResultSchema),
});

// Product Screening Result schema
export const ProductScreeningResultSchema = z.object({
  id: z.string().uuid(),
  screening_result_id: z.string().uuid(),
  database_producxt_id: z.string().uuid(),
  hit_count: z.number().default(0),
  new_total: z.number().default(0),
  last_screening_date: z.string().datetime().optional(),
  is_base_snapshot: z.boolean().default(false),
  all_results: z.string().optional().nullable(),
  diff_compressed: z.string().optional().nullable(),
  results_hash: z.string().optional().nullable(),
  diff_added_count: z.number().default(0),
  diff_removed_count: z.number().default(0),
  diff_modified_count: z.number().default(0),
  doc_info: z.record(z.any()).optional().nullable(),
  created_at: z.string().datetime().optional(),
  ai_summary: AISummarySchema.optional().nullable(),
  screening_timestamp: z.string().datetime().optional(),
  screening_reason: z.string().optional().nullable(),
  screened_by: z.string().optional().nullable(),
  is_latest: z.boolean().optional(),
  denied_party_results: z.any().optional(),
});

// Product Screening History Response schema
export const ProductScreenHisResSchema = z.object({
  latestResult: ProductScreeningResultSchema,
  latestTariffResult: z.any(),
  deltas: z.array(ProductScreeningResultSchema),
});

export type IScreeningResult = z.infer<typeof ScreeningResultSchema>;
export type IScreeningResultRow = z.infer<typeof ScreeningResultRowSchema>;
export type IAIResults = z.infer<typeof AIResultsSchema>;
export type IEntityScreening = z.infer<typeof EntityScreeningSchema>;
export type IProductScreening = z.infer<typeof ProductScreeningSchema>;
export type IRunScreeningRequest = z.infer<typeof RunScreeningRequestSchema>;
export type IRunEntityScreeningRequest = z.infer<
  typeof RunEntityScreeningRequestSchema
>;
export type IRunProductScreeningRequest = z.infer<
  typeof RunProductScreeningRequestSchema
>;
export type IRunSelectEntitiesRequest = z.infer<
  typeof RunSelectEntitiesRequestSchema
>;
export type IRunTariffCheckRequest = z.infer<
  typeof RunTariffCheckRequestSchema
>;
export type IRunProductCheckRequest = z.infer<
  typeof RunProductCheckRequestSchema
>;
export type IEntityScreeningResult = z.infer<
  typeof EntityScreeningResultSchema
>;
export type IEntityScreeningHistoryResponse = z.infer<
  typeof EntityScreenHisResSchema
>;
export type IProductScreeningHistoryResponse = z.infer<
  typeof ProductScreenHisResSchema
>;
